import { gsap } from 'gsap';
import { logger } from '@/common/services/config';

export interface MenuHoverConfig {
  duration?: number;
  ease?: string;
  fadeOutDuration?: number;
  fadeOutEase?: string;
  hoverColor?: string;
  borderRadius?: string;
  shadow?: string;
  border?: string;
  zIndex?: number | string;
}

// Define a concise alias to avoid long inline generic types in annotations
type MenuHoverResolvedConfig = Required<
  Omit<MenuHoverConfig, 'hoverColor' | 'borderRadius' | 'shadow' | 'border' | 'zIndex'>
>;

export class MenuHoverAnimation {
  private static defaultConfig: MenuHoverResolvedConfig = {
    duration: 0.5,
    ease: 'power3.out',
    fadeOutDuration: 0.5,
    fadeOutEase: 'power3.in',
  };

  private hoverBlock: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private config: MenuHoverConfig = {};

  constructor(container: HTMLElement | string, config: MenuHoverConfig = {}) {
    this.container =
      typeof container === 'string'
        ? (document.querySelector(container) as HTMLElement | null)
        : container;

    if (!this.container) {
      logger.warn('Container not found for MenuHoverAnimation');
      return;
    }

    this.config = { ...MenuHoverAnimation.defaultConfig, ...config };
    this.init();
  }

  private init(): void {
    if (!this.container) return;

    // S'assurer que le conteneur a une position relative
    if (getComputedStyle(this.container).position === 'static') {
      this.container.style.position = 'relative';
    }

    // Créer le bloc d'animation s'il n'existe pas
    this.hoverBlock = this.container.querySelector('.menu-hover-block');
    if (!this.hoverBlock) {
      this.hoverBlock = document.createElement('div');
      this.hoverBlock.className = 'menu-hover-block';
      this.setupHoverBlockStyles();
      this.container.insertBefore(this.hoverBlock, this.container.firstChild);
    }
  }

  private setupHoverBlockStyles(): void {
    if (!this.hoverBlock) return;

    // Styles de base pour le bloc d'animation
    Object.assign(this.hoverBlock.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      pointerEvents: 'none',
      zIndex: (this.config.zIndex ?? 1000).toString(),
      opacity: '0',
      background: this.config.hoverColor || '#FF6B6B',
      borderRadius: this.config.borderRadius || '3vmin',
      boxShadow: this.config.shadow || 'none',
      border: this.config.border || 'none',
      willChange: 'transform, width, height, opacity',
      transition: 'none',
    } as CSSStyleDeclaration);
  }

  /**
   * Animation au survol d'un élément
   */
  animateHover(targetElement: HTMLElement): void {
    if (!this.hoverBlock || !this.container || !targetElement) return;

    // Kill any existing animations to prevent conflicts
    gsap.killTweensOf(this.hoverBlock);

    const containerRect = this.container.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();

    // Calculer la position relative
    const left = targetRect.left - containerRect.left;
    const top = targetRect.top - containerRect.top;

    // Obtenir les dimensions
    const width = targetElement.offsetWidth;
    const height = targetElement.offsetHeight;

    // Animation GSAP
    gsap.to(this.hoverBlock, {
      width: width,
      height: height,
      x: left,
      y: top,
      opacity: 1,
      duration: this.config.duration,
      ease: this.config.ease,
    });
  }

  /**
   * Animation de sortie
   */
  animateOut(): void {
    if (!this.hoverBlock) return;

    // Kill any existing animations on this element first
    gsap.killTweensOf(this.hoverBlock);

    gsap.to(this.hoverBlock, {
      opacity: 0,
      duration: this.config.fadeOutDuration,
      ease: this.config.fadeOutEase,
    });
  }

  /**
   * Force immediate cleanup - kills all animations and sets opacity to 0
   */
  forceCleanup(): void {
    if (!this.hoverBlock) return;

    // Kill all animations immediately
    gsap.killTweensOf(this.hoverBlock);

    // Set opacity to 0 immediately without animation
    gsap.set(this.hoverBlock, {
      opacity: 0,
    });
  }

  /**
   * Maintient l'état hover sans l'animer
   * Utile pour garder l'état actif pendant qu'un dropdown est ouvert
   */
  keepHoverState(): void {
    if (!this.hoverBlock) return;

    // Maintient simplement l'opacité à 1 sans animation
    gsap.set(this.hoverBlock, {
      opacity: 1,
    });
  }

  /**
   * Mise à jour de la configuration
   */
  updateConfig(newConfig: MenuHoverConfig): void {
    this.config = { ...this.config, ...newConfig };
    if (this.hoverBlock && newConfig.hoverColor) {
      this.hoverBlock.style.background = newConfig.hoverColor;
    }
    if (this.hoverBlock && newConfig.borderRadius) {
      this.hoverBlock.style.borderRadius = newConfig.borderRadius;
    }
    if (this.hoverBlock && newConfig.shadow) {
      this.hoverBlock.style.boxShadow = newConfig.shadow;
    }
    if (this.hoverBlock && newConfig.border) {
      this.hoverBlock.style.border = newConfig.border;
    }
    if (this.hoverBlock && newConfig.zIndex !== undefined) {
      this.hoverBlock.style.zIndex = String(newConfig.zIndex);
    }
  }

  /**
   * Destruction de l'instance
   */
  destroy(): void {
    if (this.hoverBlock && this.container) {
      this.container.removeChild(this.hoverBlock);
    }
    this.hoverBlock = null;
    this.container = null;
  }

  /**
   * Méthode statique pour créer rapidement une instance
   */
  static create(container: HTMLElement | string, config?: MenuHoverConfig): MenuHoverAnimation {
    return new MenuHoverAnimation(container, config);
  }
}

export default MenuHoverAnimation;
